import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  CreditCard, Building2, Copy, Check, Clock, CheckCircle,
  Wallet, FileText, CalendarClock, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import IyzicoPaymentModal from '../../components/IyzicoPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

// KDV dahil sipariş tutarı (total_amount = KDV hariç net; KDV item'lardan hesaplanır)
// Backend cari hareket ve Iyzico tahsilatı ile aynı mantık.
const orderGrossTotal = (o) => {
  const items = o?.items || [];
  const kdv = items.reduce(
    (s, i) => s + (i.unit_price || 0) * (i.quantity || 0) * ((i.kdv_orani || 0) / 100),
    0
  );
  return Math.round(((o?.total_amount || 0) + kdv) * 100) / 100;
};

const PAYMENT_STATUS = {
  pending: { label: 'Bekliyor', color: 'bg-amber-50 text-amber-700' },
  paid: { label: 'Ödendi', color: 'bg-green-50 text-green-700' },
  failed: { label: 'Başarısız', color: 'bg-[#eef0f7] text-[#141a3d]' },
  on_account: { label: 'Cari', color: 'bg-gray-100 text-gray-700' },
};

const PAYMENT_METHOD = {
  bank_transfer: { label: 'Havale/EFT', icon: Building2 },
  credit_card: { label: 'Kredi Kartı', icon: CreditCard },
  on_account: { label: 'Cariye Yazıldı', icon: FileText },
};

export default function DealerPayments() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedIban, setCopiedIban] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [payOrder, setPayOrder] = useState(null); // order being paid via Iyzico
  const [manualOpen, setManualOpen] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [cariBalance, setCariBalance] = useState(0);
  const [paymentPromises, setPaymentPromises] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const [ordersRes, cariRes, promisesRes, bankRes] = await Promise.all([
          axios.get(`${API_URL}/api/orders?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/dealer/account-movements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { summary: { balance: 0 } } })),
          axios.get(`${API_URL}/api/dealer/payment-promises`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/public/bank-info`).catch(() => ({ data: { accounts: [] } })),
        ]);
        setOrders(ordersRes.data.orders || []);
        setCariBalance(cariRes.data?.summary?.balance || 0);
        setPaymentPromises(Array.isArray(promisesRes.data) ? promisesRes.data : []);
        // Bank info comes from Admin Settings (system_settings.banka_*) — single source of truth
        setBankAccounts(bankRes.data?.accounts || []);
      } catch { toast.error('Veriler yüklenemedi'); }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, [token]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    setCopiedIban(id);
    toast.success('IBAN kopyalandı');
    setTimeout(() => setCopiedIban(null), 2000);
  };

  const pendingPayments = orders.filter(o => o.payment_status === 'pending' && o.payment_method === 'bank_transfer');
  const totalPending = pendingPayments.reduce((sum, o) => sum + orderGrossTotal(o), 0);
  const paidOrders = orders.filter(o => o.payment_status === 'paid');
  const totalPaid = paidOrders.reduce((sum, o) => sum + orderGrossTotal(o), 0);
  const onAccountOrders = orders.filter(o => o.payment_method === 'on_account');

  // Açık (ödenmemiş) tüm siparişler
  const openOrders = orders.filter(o => o.payment_status === 'pending');
  const totalOpenOrders = openOrders.reduce((sum, o) => sum + orderGrossTotal(o), 0);

  // Aktif ödeme sözleri
  const activePromises = (paymentPromises || []).filter(p => p.status !== 'paid' && !p.is_deleted);
  const totalPromised = activePromises.reduce((sum, p) => sum + (p.amount || 0), 0);
  const nearestPromise = activePromises.length > 0
    ? [...activePromises].sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))[0]
    : null;

  const tabOrders = activeTab === 'pending' ? pendingPayments : activeTab === 'paid' ? paidOrders : onAccountOrders;

  // İlk kart: Ödeme sözü varsa onu göster, yoksa açık siparişler toplamı
  const firstCard = nearestPromise
    ? {
        label: 'Ödeme Sözü',
        value: formatCurrency(totalPromised),
        sub: `${activePromises.length} söz · En yakın: ${nearestPromise.due_date ? new Date(nearestPromise.due_date).toLocaleDateString('tr-TR') : '-'}`,
        icon: CalendarClock,
        warn: true,
      }
    : {
        label: 'Açık Siparişler',
        value: formatCurrency(totalOpenOrders),
        sub: `${openOrders.length} ödenmemiş sipariş`,
        icon: Clock,
        warn: totalOpenOrders > 0,
      };

  const summaryStats = [
    firstCard,
    { label: 'Toplam Ödenen', value: formatCurrency(totalPaid), sub: `${paidOrders.length} sipariş`, icon: CheckCircle },
    { label: 'Cari Borç', value: formatCurrency(cariBalance), sub: cariBalance > 0 ? 'Detay için tıklayın →' : 'Hesabınız dengede', icon: Wallet, warn: cariBalance > 0, onClick: () => navigate('/dealer/account') },
  ];

  return (
    <div className="space-y-3 sm:space-y-5" data-testid="dealer-payments">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Ödemeler</h1>
        <p className="text-sm text-gray-500 mt-1">Ödeme bilgileri ve geçmişiniz</p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
        {summaryStats.map((stat, i) => (
          <div
            key={i}
            onClick={stat.onClick}
            className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 ${stat.onClick ? 'cursor-pointer hover:border-[#8b96c4] hover:shadow-md transition-all' : ''}`}
            data-testid={`payment-summary-${i}`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-gray-500 truncate flex items-center gap-1">
                  {stat.label}
                  {stat.onClick && <ArrowRight className="w-3 h-3 text-gray-400 hidden sm:inline" />}
                </p>
                <p className={`text-lg sm:text-2xl font-bold mt-0.5 tabular-nums ${stat.warn ? 'text-[#1a224f]' : 'text-gray-900'}`}>{stat.value}</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5 truncate">{stat.sub}</p>
              </div>
              <div className={`rounded-lg p-2 sm:p-2.5 shrink-0 ml-2 ${stat.warn ? 'bg-[#eef0f7] text-[#1a224f]' : 'bg-gray-100 text-gray-500'}`}>
                <stat.icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Manuel Kredi Kartı Ödemesi */}
      <div className="bg-gradient-to-br from-[#eef0f7] to-orange-50 border border-[#b3bbdc] rounded-lg overflow-hidden" data-testid="manual-payment-card">
        <div className="px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4">
          <div className="rounded-xl bg-[#1a224f] text-white p-2.5 sm:p-3 shrink-0">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm sm:text-base font-bold text-gray-900">Hızlı Kredi Kartı Ödemesi</h2>
            <p className="text-[11px] sm:text-xs text-gray-600 mt-0.5">Tutarı girip kredi kartınızla anında ödeme yapın · Iyzico güvenli ödeme · Taksit seçeneği</p>
          </div>
        </div>
        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
            <div className="flex-1">
              <label className="block text-[11px] font-medium text-gray-700 mb-1">Ödenecek Tutar (TRY)</label>
              <div className="relative">
                <input
                  type="number" step="0.01" min="0.01"
                  value={manualAmount}
                  onChange={(e) => setManualAmount(e.target.value)}
                  placeholder="Örn: 1500.00"
                  className="w-full pl-3 pr-10 py-2.5 border border-gray-200 rounded-lg text-base font-semibold tabular-nums bg-white focus:outline-none focus:ring-2 focus:ring-[#3a4680]/20 focus:border-[#5b67a0]"
                  data-testid="manual-amount-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">₺</span>
              </div>
            </div>
            <button
              onClick={() => {
                const amt = parseFloat(manualAmount);
                if (!amt || amt <= 0) { toast.error('Geçerli bir tutar girin'); return; }
                setManualOpen(true);
              }}
              disabled={!manualAmount || parseFloat(manualAmount) <= 0}
              className="px-4 py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-semibold hover:bg-[#141a3d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shrink-0"
              data-testid="manual-pay-btn"
            >
              <CreditCard className="w-4 h-4" />Kart ile Öde
              {manualAmount && parseFloat(manualAmount) > 0 && (
                <span className="hidden sm:inline tabular-nums">· {formatCurrency(parseFloat(manualAmount))}</span>
              )}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] text-gray-500">Hızlı seç:</span>
            {cariBalance > 0 && (
              <button onClick={() => setManualAmount(cariBalance.toFixed(2))} className="text-[11px] px-2 py-0.5 bg-[#d8dcee] border border-[#8b96c4] text-[#0f142e] font-semibold rounded-full hover:bg-[#b3bbdc]" data-testid="quick-amount-cari">
                Cari Borç: {formatCurrency(cariBalance)}
              </button>
            )}
            {orders.length > 0 && (
              <button onClick={() => setManualAmount(orderGrossTotal(orders[0]).toFixed(2))} className="text-[11px] px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 font-medium rounded-full hover:bg-blue-100" data-testid="quick-amount-last">
                Son Sipariş #{orders[0]?.order_number}: {formatCurrency(orderGrossTotal(orders[0]))}
              </button>
            )}
            {totalPending > 0 && (
              <button onClick={() => setManualAmount(totalPending.toFixed(2))} className="text-[11px] px-2 py-0.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50" data-testid="quick-amount-total">
                Tüm bekleyenler: {formatCurrency(totalPending)}
              </button>
            )}
            {pendingPayments.slice(0, 2).map(o => (
              <button key={o.id} onClick={() => setManualAmount(orderGrossTotal(o).toFixed(2))} className="text-[11px] px-2 py-0.5 bg-white border border-gray-200 rounded-full hover:bg-gray-50" data-testid={`quick-amount-${o.order_number}`}>
                #{o.order_number}: {formatCurrency(orderGrossTotal(o))}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bank Accounts */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-semibold text-gray-900">Havale/EFT Bilgileri</h2>
        </div>
        <div className="p-4">
          <p className="text-xs text-gray-500 mb-3">
            Havale ile ödeme yaparken aşağıdaki hesap bilgilerini kullanabilirsiniz. Açıklama kısmına sipariş numaranızı yazmayı unutmayın.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bankAccounts.length === 0 && (
              <div className="col-span-full text-center py-6 text-sm text-gray-400">
                {loading ? 'Yükleniyor...' : 'Aktif banka hesabı bulunmuyor'}
              </div>
            )}
            {bankAccounts.map((account, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors" data-testid={`bank-account-${index}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm text-gray-900">{account.bank}</span>
                  <span className="text-[10px] text-gray-500">{account.branch}</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div><span className="text-gray-500">Hesap:</span> <span className="text-gray-900">{account.accountName}</span></div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-gray-500">IBAN:</span>{' '}
                      <span className="font-mono text-gray-900 text-[11px]">{account.iban}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(account.iban, index)}
                      className="p-1.5 hover:bg-gray-100 rounded transition-colors shrink-0"
                      title="IBAN'ı kopyala"
                      data-testid={`copy-iban-${index}`}
                    >
                      {copiedIban === index ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center gap-2 overflow-x-auto">
          {[
            { id: 'pending', label: 'Bekleyen', count: pendingPayments.length },
            { id: 'paid', label: 'Ödenen', count: paidOrders.length },
            { id: 'on_account', label: 'Cari', count: onAccountOrders.length },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'bg-[#eef0f7] text-[#141a3d] border border-[#b3bbdc]' : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
              data-testid={`payment-tab-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a224f]" />
          </div>
        ) : tabOrders.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Bu kategoride ödeme bulunmuyor</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Sipariş No</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Yöntem</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Tutar</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-28">Durum</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-28">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {tabOrders.map((order, i) => {
                    const ps = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending;
                    const pm = PAYMENT_METHOD[order.payment_method] || PAYMENT_METHOD.bank_transfer;
                    const MIcon = pm.icon;
                    const canPay = order.payment_status === 'pending';
                    return (
                      <tr key={order.id} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`payment-row-${order.order_number}`}>
                        <td className="px-2 py-2.5 text-center text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-3 py-2.5 font-medium text-gray-900">#{order.order_number}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs">{formatDate(order.created_at)}</td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center gap-1.5 text-gray-600 text-xs">
                            <MIcon className="w-3.5 h-3.5 text-gray-400" />{pm.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatCurrency(orderGrossTotal(order))}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${ps.color}`}>
                            {ps.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {canPay && (
                            <button onClick={() => setPayOrder(order)} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#1a224f] text-white rounded-md text-[11px] font-medium hover:bg-[#141a3d]" data-testid={`pay-now-${order.order_number}`}>
                              <CreditCard className="w-3 h-3" />Şimdi Öde
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {tabOrders.map(order => {
                const ps = PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending;
                const pm = PAYMENT_METHOD[order.payment_method] || PAYMENT_METHOD.bank_transfer;
                const canPay = order.payment_status === 'pending';
                return (
                  <div key={order.id} className="p-3 flex items-center justify-between gap-3" data-testid={`payment-mobile-${order.order_number}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900">#{order.order_number}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${ps.color}`}>{ps.label}</span>
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">{pm.label} · {formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <p className="text-sm font-bold text-gray-900 tabular-nums">{formatCurrency(orderGrossTotal(order))}</p>
                      {canPay && (
                        <button onClick={() => setPayOrder(order)} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1a224f] text-white rounded text-[10px] font-medium" data-testid={`pay-now-mobile-${order.order_number}`}>
                          <CreditCard className="w-2.5 h-2.5" />Öde
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <IyzicoPaymentModal
        open={!!payOrder}
        onClose={() => setPayOrder(null)}
        kind="order"
        orderId={payOrder?.id}
        amount={payOrder ? orderGrossTotal(payOrder) : 0}
        title="Sipariş Ödemesi"
        subtitle={payOrder ? `Sipariş #${payOrder.order_number}` : ''}
        onSuccess={() => {
          setPayOrder(null);
          toast.success('Ödeme alındı');
          setTimeout(() => window.location.reload(), 1200);
        }}
      />
      <IyzicoPaymentModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        kind="cari"
        amount={parseFloat(manualAmount) || 0}
        title="Manuel Kredi Kartı Ödemesi"
        subtitle="Iyzico güvenli ödeme · Cari hesabınıza işlenir"
        onSuccess={() => {
          setManualOpen(false);
          setManualAmount('');
          toast.success('Ödeme alındı, cari hesabınız güncelleniyor...');
          setTimeout(() => window.location.reload(), 1500);
        }}
      />
    </div>
  );
}
